import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ChevronLeft,
  Loader2,
  User,
  Shield,
  FileText,
  Wallet,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Download,
  MessageSquare,
  Send,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

const AdminUserDetailPage = () => {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const { formatCurrency } = useI18n();
  const navigate = useNavigate();
  
  const [userData, setUserData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [adminNotes, setAdminNotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [newNote, setNewNote] = useState('');
  const [newMessage, setNewMessage] = useState({ subject: '', content: '', priority: 'normal' });
  const [updating, setUpdating] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchUserData();
    fetchMessages();
  }, [userId]);

  const fetchMessages = async () => {
    try {
      const response = await api.get(`/admin/users/${userId}/messages`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.subject.trim() || !newMessage.content.trim()) {
      toast.error('Por favor complete el asunto y mensaje');
      return;
    }
    
    setSendingMessage(true);
    try {
      await api.post(`/admin/users/${userId}/messages`, {
        subject: newMessage.subject,
        content: newMessage.content,
        priority: newMessage.priority
      });
      toast.success('Mensaje enviado correctamente');
      setNewMessage({ subject: '', content: '', priority: 'normal' });
      fetchMessages();
    } catch (error) {
      toast.error('Error al enviar mensaje');
    } finally {
      setSendingMessage(false);
    }
  };

  const fetchUserData = async () => {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      setUserData(response.data.user);
      setDocuments(response.data.documents || []);
      setCommitments(response.data.commitments || []);
      setAdminNotes(response.data.admin_notes || []);
      setNewStatus(response.data.user.verification_status || 'pending');
    } catch (error) {
      toast.error('Error al cargar usuario');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const updateVerificationStatus = async () => {
    setUpdating(true);
    try {
      await api.put(`/admin/users/${userId}/verification`, {
        status: newStatus,
        notes: statusNotes,
      });
      toast.success('Estado de verificación actualizado');
      fetchUserData();
      setStatusNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setUpdating(false);
    }
  };

  const addAdminNote = async () => {
    if (!newNote.trim()) return;
    
    setAddingNote(true);
    try {
      await api.post('/admin/notes', {
        entity_type: 'user',
        entity_id: userId,
        note: newNote,
        is_internal: true,
      });
      toast.success('Nota agregada');
      setNewNote('');
      fetchUserData();
    } catch (error) {
      toast.error('Error al agregar nota');
    } finally {
      setAddingNote(false);
    }
  };

  const requestDocument = async (category, description) => {
    try {
      await api.post(`/admin/users/${userId}/request-document`, {
        category,
        description,
        required: true,
      });
      toast.success('Documento solicitado');
    } catch (error) {
      toast.error('Error al solicitar documento');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Pendiente', icon: Clock },
      in_review: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'En Revisión', icon: Clock },
      approved: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Aprobado', icon: CheckCircle2 },
      rejected: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Rechazado', icon: XCircle },
      more_info_required: { className: 'bg-orange-500/10 text-orange-500 border-orange-500/20', label: 'Info Requerida', icon: AlertCircle },
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge variant="outline" className={`${style.className} text-lg px-4 py-2`}>
        <Icon className="h-4 w-4 mr-2" />
        {style.label}
      </Badge>
    );
  };

  const getCategoryLabel = (category) => {
    const labels = {
      id_document: 'Identificación',
      address_proof: 'Comprobante de Domicilio',
      tax: 'Documentos Fiscales',
      kyc: 'Verificación KYC',
      proof_of_funds: 'Prueba de Fondos',
      incorporation: 'Acta Constitutiva',
      other: 'Otro',
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="text-center py-16">
        <User className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
        <p className="text-muted-foreground">Usuario no encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-user-detail-page">
      {/* Back Link */}
      <Link 
        to="/admin/users" 
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
        Volver a usuarios
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-4">
            {userData.picture ? (
              <img src={userData.picture} alt="" className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center text-2xl text-muted-foreground">
                {userData.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            )}
            <div>
              <h1 className="heading-3">{userData.name}</h1>
              <p className="text-muted-foreground">{userData.email}</p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {getStatusBadge(userData.verification_status)}
            <Badge variant="outline" className="capitalize">{userData.role?.replace('_', ' ')}</Badge>
            <Badge variant="outline" className="capitalize">{userData.account_type?.replace('_', ' ') || 'Sin tipo'}</Badge>
          </div>
        </div>

        {/* Verification Actions */}
        <Card className="card-dashboard md:w-96">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5" strokeWidth={1.5} />
              Acción de Verificación
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Cambiar Estado</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="verification-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="in_review">En Revisión</SelectItem>
                  <SelectItem value="approved">Aprobado</SelectItem>
                  <SelectItem value="rejected">Rechazado</SelectItem>
                  <SelectItem value="more_info_required">Más Información Requerida</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notas para el usuario (opcional)</Label>
              <Textarea
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Estas notas serán visibles para el usuario..."
                rows={2}
              />
            </div>
            
            <Button
              onClick={updateVerificationStatus}
              disabled={updating || newStatus === userData.verification_status}
              className="w-full btn-primary"
              data-testid="update-verification"
            >
              {updating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
              )}
              Actualizar Estado
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0">
          <TabsTrigger 
            value="profile" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
          >
            Perfil
          </TabsTrigger>
          <TabsTrigger 
            value="documents" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
          >
            Documentos ({documents.length})
          </TabsTrigger>
          <TabsTrigger 
            value="commitments" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
          >
            Inversiones ({commitments.length})
          </TabsTrigger>
          <TabsTrigger 
            value="notes" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
          >
            Notas Internas ({adminNotes.length})
          </TabsTrigger>
          <TabsTrigger 
            value="messages" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
          >
            Mensajes ({messages.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-lg">Información Personal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Teléfono</span>
                  <span>{userData.phone || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">RFC</span>
                  <span>{userData.tax_id || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Nacionalidad</span>
                  <span>{userData.nationality || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fecha de nacimiento</span>
                  <span>{userData.date_of_birth || '-'}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-lg">Dirección</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Dirección</span>
                  <span className="text-right">{userData.address || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ciudad</span>
                  <span>{userData.city || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Estado</span>
                  <span>{userData.state || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">País</span>
                  <span>{userData.country || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">C.P.</span>
                  <span>{userData.postal_code || '-'}</span>
                </div>
              </CardContent>
            </Card>

            {/* Referral Information */}
            <Card className="card-dashboard border-primary/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  Información de Referido
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tipo de Referido</span>
                  <span className="capitalize">
                    {userData.referral_type === 'executive_contact' && 'Ejecutivo de SBD'}
                    {userData.referral_type === 'client_referral' && 'Cliente/Inversionista'}
                    {userData.referral_type === 'personal_referral' && 'Referencia Personal'}
                    {userData.referral_type === 'professional_network' && 'Red Profesional'}
                    {!userData.referral_type && '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {userData.referral_type === 'executive_contact' ? 'Ejecutivo' : 'Referido por'}
                  </span>
                  <span className="font-medium">{userData.referral_name || '-'}</span>
                </div>
                {userData.referral_details && (
                  <div className="pt-2 border-t border-border">
                    <span className="text-muted-foreground text-sm">Detalles adicionales:</span>
                    <p className="text-sm mt-1">{userData.referral_details}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {userData.account_type === 'entity' && (
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-lg">Información de la Empresa</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Razón Social</span>
                    <span>{userData.company_name || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">RFC Empresa</span>
                    <span>{userData.company_tax_id || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Representante Legal</span>
                    <span>{userData.company_legal_rep_name || '-'}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-lg">Perfil de Inversión</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Experiencia</span>
                  <span className="capitalize">{userData.investment_experience?.replace('_', ' ') || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tolerancia al riesgo</span>
                  <span className="capitalize">{userData.risk_tolerance || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fuente de fondos</span>
                  <span className="capitalize">{userData.source_of_funds?.replace('_', ' ') || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Riesgos reconocidos</span>
                  <span>{userData.acknowledged_risks ? '✓ Sí' : '✗ No'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Inversionista acreditado</span>
                  <span>{userData.is_accredited ? '✓ Sí' : '✗ No'}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="pt-6">
          <Card className="card-dashboard">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Documentos del Usuario</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const desc = prompt('Descripción del documento solicitado:');
                  if (desc) requestDocument('other', desc);
                }}
              >
                Solicitar Documento
              </Button>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
                  No hay documentos
                </p>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div 
                      key={doc.document_id}
                      className="flex items-center justify-between p-4 border border-border rounded-sm"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                        <div>
                          <p className="font-medium">{doc.description || doc.original_filename}</p>
                          <p className="text-xs text-muted-foreground">
                            {getCategoryLabel(doc.category)} • {new Date(doc.created_at).toLocaleDateString('es-MX')}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Download className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="commitments" className="pt-6">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg">Compromisos de Inversión</CardTitle>
            </CardHeader>
            <CardContent>
              {commitments.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  <Wallet className="h-8 w-8 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
                  No hay compromisos
                </p>
              ) : (
                <div className="space-y-3">
                  {commitments.map((c) => (
                    <div 
                      key={c.commitment_id}
                      className="flex items-center justify-between p-4 border border-border rounded-sm"
                    >
                      <div>
                        <p className="font-medium">{c.opportunity_title || c.opportunity_id}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(c.created_at).toLocaleDateString('es-MX')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-primary">
                          {formatCurrency(c.amount, c.currency)}
                        </p>
                        <Badge variant="outline" className="capitalize">{c.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes" className="pt-6">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" strokeWidth={1.5} />
                Notas Internas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Note Form */}
              <div className="flex gap-2">
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Agregar nota interna (no visible para el usuario)..."
                  rows={2}
                  className="flex-1"
                />
                <Button
                  onClick={addAdminNote}
                  disabled={addingNote || !newNote.trim()}
                  className="self-end"
                >
                  {addingNote ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" strokeWidth={1.5} />
                  )}
                </Button>
              </div>

              {/* Notes List */}
              {adminNotes.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  No hay notas internas
                </p>
              ) : (
                <div className="space-y-3">
                  {adminNotes.map((note) => (
                    <div 
                      key={note.note_id}
                      className="p-4 bg-muted/30 rounded-sm"
                    >
                      <p className="text-sm mb-2">{note.note}</p>
                      <p className="text-xs text-muted-foreground">
                        {note.created_by_name} • {new Date(note.created_at).toLocaleString('es-MX')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="pt-6">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Send className="h-5 w-5" strokeWidth={1.5} />
                Enviar Mensaje al Usuario
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Asunto *</Label>
                  <input
                    type="text"
                    value={newMessage.subject}
                    onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                    placeholder="Asunto del mensaje..."
                    className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prioridad</Label>
                  <Select
                    value={newMessage.priority}
                    onValueChange={(value) => setNewMessage({ ...newMessage, priority: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Baja</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Mensaje *</Label>
                <Textarea
                  value={newMessage.content}
                  onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                  placeholder="Escriba su mensaje aquí... El usuario recibirá una notificación."
                  rows={4}
                />
              </div>
              <Button
                onClick={sendMessage}
                disabled={sendingMessage || !newMessage.subject.trim() || !newMessage.content.trim()}
                className="gap-2"
              >
                {sendingMessage ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" strokeWidth={1.5} />
                )}
                Enviar Mensaje
              </Button>
            </CardContent>
          </Card>

          {/* Messages History */}
          <Card className="card-dashboard mt-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" strokeWidth={1.5} />
                Historial de Mensajes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
                  No hay mensajes con este usuario
                </p>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div 
                      key={msg.message_id}
                      className={`p-4 rounded-sm border ${
                        msg.type === 'user_to_admin' 
                          ? 'border-primary/30 bg-primary/5' 
                          : 'border-border bg-muted/30'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={msg.type === 'user_to_admin' ? 'default' : 'outline'} className="text-xs">
                            {msg.type === 'user_to_admin' ? 'Respuesta del Usuario' : 'Mensaje Enviado'}
                          </Badge>
                          {msg.priority === 'high' && (
                            <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-xs">Alta</Badge>
                          )}
                          {msg.priority === 'urgent' && (
                            <Badge className="bg-red-500/10 text-red-500 border-red-500/20 text-xs">Urgente</Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(msg.created_at).toLocaleString('es-MX')}
                        </span>
                      </div>
                      <h4 className="font-medium text-sm mb-1">{msg.subject}</h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{msg.content}</p>
                      {msg.type === 'admin_to_user' && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Enviado por: {msg.from_admin_name}
                          {msg.read && ` • Leído: ${new Date(msg.read_at).toLocaleString('es-MX')}`}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminUserDetailPage;
